// Ported from dev-scrips-pm-react src/billing/services/use-cashier-shifts.ts (PROD-3312 appointment Billing tab).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/shared/api/api-client';
import { CASHIER_BASE } from '@/shared/api/billing-client';
import { showSuccess } from '@/shared/lib/toast-helpers';
import type {
  CashierShiftListResponse,
  CashierShiftModel,
  ExportCsvResponse,
  OpenCashierShiftRequest,
} from '@/shared/types/billing';

/**
 * Cashier Shift list + lifecycle hooks (open / close / per-shift CSV).
 *
 * Endpoints (mirror Flutter `billing_data_source_impl.dart:1041-1138`):
 *   GET  /api/CashierShift?pageSize&pageNumber&Q&ShiftDateFrom&ShiftDateTo
 *                         &ShiftUsers&Practices                 — list (verified 2026-05-12)
 *   POST /api/CashierShift/Open       body={openingBalance,category} — open
 *   PUT  /api/CashierShift/Close/{id} body={}                         — close
 *   POST /api/CashierShift/{id}/ExportCsv body={}                     — per-shift CSV
 *
 * Wire-shape gotchas:
 * - Query params: `pageNumber` not `pageNum`. Filter casing is PascalCase
 *   (`ShiftDateFrom`, `ShiftUsers`) — backend quirk.
 * - Dates ship without the trailing `Z` (no-Z ISO). Use the locale-stable
 *   `.toISOString().replace(/Z$/, '')` trick when sending date filters.
 * - `status` is the closed enum `"Open" | "Closed"`.
 * - The Jira description said `POST /CashierShift` for open and
 *   `DELETE /CashierShift/{id}` for close — both wrong per Flutter source.
 */

const KEY_ROOT = ['cashier-shifts'] as const;

export interface CashierShiftListParams {
  query?: string;
  pageNumber?: number;
  pageSize?: number;
  shiftUserIds?: string[];
  practiceIds?: string[];
  /** Inclusive lower bound, ISO no-Z. */
  shiftDateFrom?: string;
  /** Inclusive upper bound, ISO no-Z. */
  shiftDateTo?: string;
}

function listKey(p: CashierShiftListParams) {
  return [
    ...KEY_ROOT,
    'list',
    p.pageNumber ?? 1,
    p.pageSize ?? 10,
    p.query ?? '',
    (p.shiftUserIds ?? []).slice().sort().join(','),
    (p.practiceIds ?? []).slice().sort().join(','),
    p.shiftDateFrom ?? '',
    p.shiftDateTo ?? '',
  ] as const;
}

function buildListQuery(p: CashierShiftListParams): string {
  const qs = new URLSearchParams();
  qs.set('pageSize', String(p.pageSize ?? 10));
  qs.set('pageNumber', String(p.pageNumber ?? 1));
  if (p.shiftDateFrom) qs.set('ShiftDateFrom', p.shiftDateFrom);
  if (p.shiftDateTo) qs.set('ShiftDateTo', p.shiftDateTo);
  if (p.query) qs.set('Q', p.query);
  // Flutter pattern: each ID becomes its own repeated query param.
  for (const id of p.shiftUserIds ?? []) qs.append('ShiftUsers', id);
  for (const id of p.practiceIds ?? []) qs.append('Practices', id);
  return qs.toString();
}

export function useCashierShifts(params: CashierShiftListParams = {}) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: ({ signal }) =>
      get<CashierShiftListResponse>(
        `${CASHIER_BASE}?${buildListQuery(params)}`,
        { signal },
      ),
    // Filter facets live in the same response — surface them via the
    // returned data, no separate query needed.
    placeholderData: (prev) => prev,
  });
}

export function useOpenCashierShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: OpenCashierShiftRequest) =>
      post<CashierShiftModel>(`${CASHIER_BASE}/Open`, request),
    onSuccess: () => {
      showSuccess('Cashier Shift opened successfully');
      void qc.invalidateQueries({ queryKey: KEY_ROOT });
    },
  });
}

export function useCloseCashierShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cashierShiftId: string) =>
      // Flutter uses PUT with empty body. Don't switch to DELETE — the
      // .NET endpoint name is literally `/Close/{id}` (not REST-conventional).
      put<CashierShiftModel>(`${CASHIER_BASE}/Close/${cashierShiftId}`, {}),
    onSuccess: () => {
      showSuccess('Cashier Shift closed successfully');
      void qc.invalidateQueries({ queryKey: KEY_ROOT });
    },
  });
}

/**
 * Trigger CSV export for a single shift. Returns a `csvLink` the caller
 * can hand to the browser as a download URL — no Blob streaming on this
 * endpoint (Flutter `exportCashierShift` POSTs `{}` and reads the link
 * from the JSON response).
 */
export function useExportCashierShiftCsv() {
  return useMutation({
    mutationFn: (cashierShiftId: string) =>
      post<ExportCsvResponse>(
        `${CASHIER_BASE}/${cashierShiftId}/ExportCsv`,
        {},
      ),
  });
}
